import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Settings as SettingsIcon,
  GridOn as GridIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { templatesApi } from '../api';
import type {
  BoardTemplate,
  TemplateSquareAssignment,
  UpdateTemplateInput,
  PayoutSchemeConfig,
  OfflinePlayer,
  NumberMode,
  CellMode,
  CellShape,
} from '../types';
import { SPORTS, DEFAULT_PAYOUT_CONFIG, getTotalCells, getCellForSquare } from '../types';
import { calculatePayoutValidation } from '../utils/payoutCalculation';
import { PayoutConfigEditor } from '../components/PayoutConfigEditor';
import { TemplateGrid } from '../components/TemplateGrid';
import { CreateOfflinePlayerDialog, BulkImportPlayersDialog } from '../components/shared';
import { ThemeSelector } from '../components/theme';
import { ConnectionSelector, type SelectablePlayer } from '../components/templates';
import { useDocumentTitle } from '../hooks/core';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const TemplateEditorPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<BoardTemplate | null>(null);
  const [assignments, setAssignments] = useState<TemplateSquareAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Settings form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sport, setSport] = useState('football');
  const [quarters, setQuarters] = useState(4);
  const [numberMode, setNumberMode] = useState<NumberMode>('single');
  const [cellMode, setCellMode] = useState<CellMode>('standard');
  const [cellShape, setCellShape] = useState<CellShape>('1x1');
  const [pricePerSquare, setPricePerSquare] = useState(10);
  const [payoutConfig, setPayoutConfig] = useState<PayoutSchemeConfig>(DEFAULT_PAYOUT_CONFIG);
  const [profitPercent, setProfitPercent] = useState(10);
  const [themeId, setThemeId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Assign mode state - selected player/connection for assignment
  const [selectedItem, setSelectedItem] = useState<SelectablePlayer | null>(null);

  // Dialog state
  const [createPlayerOpen, setCreatePlayerOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  // Track version to force ConnectionSelector refresh
  const [connectionRefreshKey, setConnectionRefreshKey] = useState(0);

  useDocumentTitle(template ? `Edit ${template.name}` : 'Edit Template');

  const fetchData = useCallback(async () => {
    if (!templateId) return;

    try {
      setLoading(true);
      const [templateData, assignmentsData] = await Promise.all([
        templatesApi.get(templateId),
        templatesApi.getAssignments(templateId),
      ]);

      setTemplate(templateData);
      setAssignments(assignmentsData);

      // Initialize form state
      setName(templateData.name);
      setDescription(templateData.description || '');
      setSport(templateData.sport);
      setQuarters(templateData.quarters);
      setNumberMode((templateData.numberMode as NumberMode) || 'single');
      setCellMode((templateData.cellMode as CellMode) || 'standard');
      setCellShape((templateData.cellShape as CellShape) || '1x1');
      setPricePerSquare(templateData.pricePerSquare);
      setPayoutConfig(templateData.payoutConfig || DEFAULT_PAYOUT_CONFIG);
      setProfitPercent(templateData.payoutConfig?.profitPercent ?? 10);
      setThemeId(templateData.themeId || null);

      setError(null);
    } catch (_err) {
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Track changes
  useEffect(() => {
    if (!template) return;

    const changed =
      name !== template.name ||
      description !== (template.description || '') ||
      sport !== template.sport ||
      quarters !== template.quarters ||
      numberMode !== (template.numberMode || 'single') ||
      cellMode !== (template.cellMode || 'standard') ||
      cellShape !== (template.cellShape || '1x1') ||
      pricePerSquare !== template.pricePerSquare ||
      JSON.stringify(payoutConfig) !== JSON.stringify(template.payoutConfig) ||
      profitPercent !== (template.payoutConfig?.profitPercent ?? 10) ||
      themeId !== (template.themeId || null);

    setHasChanges(changed);
  }, [template, name, description, sport, quarters, numberMode, cellMode, cellShape, pricePerSquare, payoutConfig, profitPercent, themeId]);

  // Compute payout validation
  const payoutValidation = useMemo(
    () => calculatePayoutValidation(pricePerSquare, profitPercent, payoutConfig, quarters, cellMode),
    [pricePerSquare, profitPercent, payoutConfig, quarters, cellMode]
  );

  const handleSave = async () => {
    if (!templateId) return;

    setSaving(true);
    try {
      const input: UpdateTemplateInput = {
        name,
        description: description || undefined,
        sport,
        quarters,
        numberMode,
        cellMode,
        cellShape,
        pricePerSquare,
        payoutConfig: {
          ...payoutConfig,
          profitPercent,
        },
        themeId: themeId,
      };

      const updated = await templatesApi.update(templateId, input);
      setTemplate(updated);
      setHasChanges(false);
      setError(null);
      setSaveSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!template) return;

    setName(template.name);
    setDescription(template.description || '');
    setSport(template.sport);
    setQuarters(template.quarters);
    setNumberMode((template.numberMode as NumberMode) || 'single');
    setCellMode((template.cellMode as CellMode) || 'standard');
    setCellShape((template.cellShape as CellShape) || '1x1');
    setPricePerSquare(template.pricePerSquare);
    setPayoutConfig(template.payoutConfig || DEFAULT_PAYOUT_CONFIG);
    setProfitPercent(template.payoutConfig?.profitPercent ?? 10);
    setThemeId(template.themeId || null);
  };

  const handleAssign = async (
    row: number,
    col: number,
    options: { offlinePlayerId?: string; playerConnectionId?: string }
  ) => {
    if (!templateId) return;

    try {
      const newAssignment = await templatesApi.setAssignment(templateId, {
        offlinePlayerId: options.offlinePlayerId,
        playerConnectionId: options.playerConnectionId,
        row,
        col,
      });

      // Update local state
      setAssignments((prev) => {
        const filtered = prev.filter((a) => !(a.row === row && a.col === col));
        return [...filtered, newAssignment];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign square');
    }
  };

  const handleClear = async (row: number, col: number) => {
    if (!templateId) return;

    try {
      await templatesApi.clearAssignment(templateId, row, col);

      // Update local state
      setAssignments((prev) => prev.filter((a) => !(a.row === row && a.col === col)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear assignment');
    }
  };

  const handleAssignModeSquareClick = (row: number, col: number) => {
    if (!selectedItem) return;
    if (selectedItem.type === 'offline') {
      handleAssign(row, col, { offlinePlayerId: selectedItem.player.id });
    } else {
      handleAssign(row, col, { playerConnectionId: selectedItem.connection.id });
    }
  };

  const handleDelete = async () => {
    if (!templateId || !template) return;

    if (!confirm(`Delete "${template.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await templatesApi.delete(templateId);
      navigate('/profile/templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  // Count assignments per player/connection
  const playerAssignmentCounts = useMemo(() => {
    const counts = new Map<string, number>();
    assignments.forEach((a) => {
      // Count by offlinePlayerId if present
      if (a.offlinePlayerId) {
        counts.set(a.offlinePlayerId, (counts.get(a.offlinePlayerId) || 0) + 1);
      }
      // Count by playerConnectionId if present
      if (a.playerConnectionId) {
        counts.set(a.playerConnectionId, (counts.get(a.playerConnectionId) || 0) + 1);
      }
    });
    return counts;
  }, [assignments]);

  // Handle player created - refresh connections
  const handlePlayerCreated = () => {
    setConnectionRefreshKey((prev) => prev + 1);
  };

  // Handle bulk import complete - refresh connections
  const handleBulkImportComplete = (players: OfflinePlayer[]) => {
    if (players.length > 0) {
      setConnectionRefreshKey((prev) => prev + 1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!template) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Template not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/profile/templates')} sx={{ mt: 2 }}>
          Back to Templates
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/profile/templates')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1">
            {template.name}
          </Typography>
          {template.description && (
            <Typography variant="body2" color="text.secondary">
              {template.description}
            </Typography>
          )}
        </Box>
        <Chip label={SPORTS.find(s => s.value === template.sport)?.label || template.sport} variant="outlined" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs - scrollable on mobile */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<SettingsIcon />} label="Settings" iconPosition="start" />
          <Tab icon={<GridIcon />} label="Assignments" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Settings Tab */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Template Details
          </Typography>
          <TextField
            label="Template Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 250))}
            helperText={`${description.length}/250 characters`}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Sport"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              sx={{ minWidth: 150 }}
              fullWidth
            >
              {SPORTS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Quarters/Periods"
              value={quarters}
              onChange={(e) => setQuarters(Number(e.target.value))}
              sx={{ minWidth: 150 }}
              fullWidth
            >
              <MenuItem value={4}>4 Quarters</MenuItem>
              <MenuItem value={3}>3 Periods</MenuItem>
              <MenuItem value={2}>2 Halves</MenuItem>
            </TextField>
            <TextField
              select
              label="Number Sets"
              value={numberMode}
              onChange={(e) => setNumberMode(e.target.value as NumberMode)}
              sx={{ minWidth: 180 }}
              fullWidth
            >
              <MenuItem value="single">Same for All Periods</MenuItem>
              <MenuItem value="half">Different per Half</MenuItem>
              <MenuItem value="quarter">Different per Quarter</MenuItem>
            </TextField>
            <TextField
              select
              label="Participation Mode"
              value={`${cellMode}-${cellShape}`}
              onChange={(e) => {
                const [newCellMode, newCellShape] = e.target.value.split('-') as [CellMode, CellShape];
                setCellMode(newCellMode);
                setCellShape(newCellShape);
                // Disable neighbor payouts when changing to non-standard cell mode
                if (newCellMode !== 'standard') {
                  setPayoutConfig(prev => ({
                    ...prev,
                    ...(prev.boxNeighborPayouts?.enabled
                      ? { boxNeighborPayouts: { ...prev.boxNeighborPayouts, enabled: false } }
                      : {}),
                    ...(prev.neighborPayouts?.enabled
                      ? { neighborPayouts: { ...prev.neighborPayouts, enabled: false } }
                      : {}),
                  }));
                }
              }}
              sx={{ minWidth: 200 }}
              fullWidth
            >
              <MenuItem value="standard-1x1">100 Squares (Standard)</MenuItem>
              <MenuItem value="paired-2x1">50 Cells - Vertical Pairs</MenuItem>
              <MenuItem value="paired-1x2">50 Cells - Horizontal Pairs</MenuItem>
              <MenuItem value="quad-2x2">25 Cells (Quad)</MenuItem>
            </TextField>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {cellMode === 'quad'
              ? 'Each participant owns 4 squares (2x2)'
              : cellMode === 'paired'
                ? 'Each participant owns 2 squares'
                : 'Each participant owns 1 square'}
          </Typography>
        </Paper>

        {/* Default Theme - placed before payout config */}
        <Typography variant="h6" gutterBottom>
          Default Theme
        </Typography>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a default theme that will be automatically applied to boards created from this template.
          </Typography>
          <ThemeSelector
            selectedThemeId={themeId}
            onSelect={setThemeId}
          />
        </Paper>

        <Typography variant="h6" gutterBottom>
          Payout Configuration
        </Typography>
        <PayoutConfigEditor
          payoutConfig={payoutConfig}
          onPayoutConfigChange={setPayoutConfig}
          pricePerSquare={pricePerSquare}
          onPricePerSquareChange={setPricePerSquare}
          profitPercent={profitPercent}
          onProfitPercentChange={setProfitPercent}
          quarters={quarters}
          totalCells={getTotalCells(cellMode)}
          cellMode={cellMode}
        />

        {/* Validation warning */}
        {!payoutValidation.isValid && (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 2, mb: 1 }}>
            {payoutValidation.isOverage
              ? `Payouts exceed the payout pot by $${Math.abs(payoutValidation.remainingDollars).toFixed(2)}. Reduce payout amounts to save.`
              : `$${payoutValidation.remainingDollars.toFixed(2)} unallocated. Allocate all funds to save.`}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Tooltip
            title={
              !payoutValidation.isValid
                ? payoutValidation.isOverage
                  ? `Cannot save: $${Math.abs(payoutValidation.remainingDollars).toFixed(2)} over budget`
                  : `Cannot save: $${payoutValidation.remainingDollars.toFixed(2)} unallocated`
                : !hasChanges
                  ? 'No changes to save'
                  : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !hasChanges || !payoutValidation.isValid}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </span>
          </Tooltip>
          {hasChanges && (
            <Button variant="outlined" onClick={handleReset} disabled={saving}>
              Reset
            </Button>
          )}
        </Box>

        {/* Danger Zone */}
        <Divider sx={{ my: 4 }} />
        <Typography variant="h6" gutterBottom color="error">
          Danger Zone
        </Typography>
        <Paper sx={{ p: 3, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete this template and all its square assignments.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete Template
          </Button>
        </Paper>
      </TabPanel>

      {/* Assignments Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Grid - full width on mobile, shrinks to fit */}
          <Box sx={{ flex: 1, order: { xs: 1, md: 0 }, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Square Assignments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click a square to assign an offline player. These assignments will be applied when creating a board from this template.
            </Typography>
            <TemplateGrid
              assignments={assignments}
              onAssign={(row, col, id) => handleAssign(row, col, { offlinePlayerId: id })}
              onClear={handleClear}
              assignModeEnabled={!!selectedItem}
              assignModePlayerId={
                selectedItem?.type === 'offline'
                  ? selectedItem.player.id
                  : undefined
              }
              assignModeConnectionId={
                selectedItem?.type === 'connection'
                  ? selectedItem.connection.id
                  : undefined
              }
              onSquareClick={handleAssignModeSquareClick}
              cellMode={cellMode}
              cellShape={cellShape}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {(() => {
                const totalCells = getTotalCells(cellMode);
                // Count unique cells assigned
                const assignedCells = new Set(
                  assignments.map(a => getCellForSquare(a.row, a.col, cellMode, cellShape))
                );
                return `${assignedCells.size} of ${totalCells} cells assigned`;
              })()}
            </Typography>
          </Box>

          {/* Connection selector - on top for mobile */}
          <Paper sx={{ width: { xs: '100%', md: 300 }, p: 2, alignSelf: 'flex-start', order: { xs: -1, md: 1 } }}>
            <ConnectionSelector
              key={connectionRefreshKey}
              selectedItem={selectedItem}
              onSelect={setSelectedItem}
              assignmentCounts={playerAssignmentCounts}
              onCreatePlayer={() => setCreatePlayerOpen(true)}
              onBulkImport={() => setBulkImportOpen(true)}
            />
          </Paper>
        </Box>
      </TabPanel>

      {/* Create Player Dialog */}
      <CreateOfflinePlayerDialog
        open={createPlayerOpen}
        onClose={() => setCreatePlayerOpen(false)}
        onCreated={handlePlayerCreated}
      />

      {/* Bulk Import Dialog */}
      <BulkImportPlayersDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onComplete={handleBulkImportComplete}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveSuccess(false)} severity="success" variant="filled">
          Template saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};
